import * as signalR from "@microsoft/signalr";
import {config} from "../config";

const monitoringBaseUrl = config.smartRoutesMonitoringBaseUrl;

let connection: signalR.HubConnection | null = null;
let startPromise: Promise<void> | null = null;

let currentIds: string[] = [];
let opVersion = 0;

export type TransportPositionDto = {
    transportId: number;
    lat: number;
    lon: number;
    speed: number;
    recordedAtUtc: string;
    extId: string;
};

type PositionsSnapshotHandler = (items: TransportPositionDto[]) => void;
type PositionUpdateHandler = (item: TransportPositionDto) => void;

const snapshotHandlers = new Set<PositionsSnapshotHandler>();
const updateHandlers = new Set<PositionUpdateHandler>();

export function onPositionsSnapshot(cb: PositionsSnapshotHandler) {
    snapshotHandlers.add(cb);
    return () => snapshotHandlers.delete(cb);
}

export function onPositionUpdate(cb: PositionUpdateHandler) {
    updateHandlers.add(cb);
    return () => updateHandlers.delete(cb);
}

function normalizeIds(ids: Array<string>): string[] {
    return Array.from(new Set(ids)).sort();
}

function arraysEqual(a: string[], b: string[]) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

async function ensureConnected() {
    if (!connection) await initSignalR();

    const conn = connection!;
    if (conn.state === signalR.HubConnectionState.Connected) return;

    // если уже стартуем — просто ждём
    if (startPromise) {
        await startPromise;
        return;
    }

    // если по какой-то причине start ещё не запускали — запускаем и ждём
    startPromise = conn.start()
        .then(() => console.log("✅ Connected"))
        .finally(() => { startPromise = null; });

    await startPromise;
}

export async function initSignalR() {
    if (connection) return;

    connection = new signalR.HubConnectionBuilder()
        .withUrl(`${monitoringBaseUrl}/hubs/transport-positions`)
        .withAutomaticReconnect()
        .build();

    connection.on("positions_snapshot", (data: TransportPositionDto[]) => {
        for (const cb of snapshotHandlers) cb(data ?? []);
        // console.log(data);
    });

    connection.on("position_update", (data: TransportPositionDto) => {
        for (const cb of updateHandlers) cb(data);
        // console.log(data);
    });

    connection.onreconnecting((error) => {
        console.log("Переподключение...", error);
    });

    connection.onreconnected(async () => {
        const conn = connection;
        if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;

        if (currentIds.length) {
            try {
                await conn.invoke("Subscribe", currentIds);
                console.log("👂 Re-subscribed to:", currentIds);
            } catch (e) {
                console.error("❌ Re-subscribe failed:", e);
            }
        }
    });

    // ВАЖНО: тут start() НЕ вызываем — пусть ensureConnected управляет этим,
    // чтобы не было параллельных start().
}

export async function subscribeToIds(ids: Array<string>) {
    const next = normalizeIds(ids);
    const myVersion = ++opVersion;

    if (arraysEqual(currentIds, next)) return;

    // дождёмся коннекта (включая ситуацию "connecting")
    await ensureConnected();

    // если пока ждали, пришёл новый вызов — выходим
    if (myVersion !== opVersion) return;

    const conn = connection!;
    // если бэк реально поддерживает Unsubscribe — снимаем старое
    if (currentIds.length) {
        try {
            await conn.invoke("Unsubscribe", currentIds);
        } catch (e) {
            console.warn("⚠️ Unsubscribe failed:", e);
        }
    }

    // если пока отписывались, пришёл новый вызов — выходим
    if (myVersion !== opVersion) return;

    currentIds = next;

    if (currentIds.length) {
        await conn.invoke("Subscribe", currentIds);
        console.log("👂 Subscribed to:", currentIds);
    } else {
        console.log("👂 Subscription cleared (empty ids)");
    }
}

export async function disconnect() {
    const conn = connection;
    if (!conn) return;

    connection = null;
    startPromise = null;
    currentIds = [];
    opVersion++;

    await conn.stop();
    console.log("🛑 Disconnected");
}
